/**
 * Handles Rush Option and Customizations for Made to Order products
 */

class MtoOptionsComponent extends HTMLElement {
  constructor() {
    super();
    this.customizations = new Map();
    this.customizationPrices = new Map();
    this.rushPrice = 0;
    this.basePrice = 0;
    this.policyAccepted = false;
    this.rushUnavailable = false;
  }

  connectedCallback() {
    this.cacheBasePrice();
    this.setupCustomizationDropdowns();
    this.setupRushOptionDropdown();
    this.setupPolicyCheckbox();
    this.updateAtcState();
  }

  cacheBasePrice() {
    const priceElement = document.querySelector('.price-container .price');
    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
      this.basePrice = parseFloat(priceText) || 0;
    }
  }

  setupCustomizationDropdowns() {
    const dropdowns = this.querySelectorAll('dropdown-element[data-group-code]');

    dropdowns.forEach(dropdown => {
      const groupCode = dropdown.dataset.groupCode;
      const options = dropdown.querySelectorAll('.dropdown__option');
      const input = dropdown.querySelector('[data-customization-input]');
      const triggerValue = dropdown.querySelector('.dropdown-trigger__value');

      options.forEach(option => {
        option.addEventListener('click', () => {
          const code = option.dataset.code;
          const value = option.dataset.value;
          const price = parseFloat(option.dataset.price) || 0;

          // Update selection state
          options.forEach(opt => {
            opt.classList.remove('dropdown__option--selected');
            opt.removeAttribute('aria-selected');
          });
          option.classList.add('dropdown__option--selected');
          option.setAttribute('aria-selected', 'true');

          // Update trigger text
          if (triggerValue) {
            triggerValue.textContent = value || 'Standard';
          }

          // Update hidden input
          if (input) {
            input.value = code ? `${groupCode}:${code}` : '';
          }

          // Update customizations map and prices
          if (code) {
            this.customizations.set(groupCode, code);
            this.customizationPrices.set(groupCode, price);
          } else {
            this.customizations.delete(groupCode);
            this.customizationPrices.delete(groupCode);
          }

          // Update combined customizations attribute
          this.updateCustomizationsAttribute();

          // Update displayed price
          this.updateDisplayedPrice();
        });
      });
    });
  }

  setupRushOptionDropdown() {
    const dropdown = this.querySelector('.dropdown-rush-option');
    if (!dropdown) return;

    const options = dropdown.querySelectorAll('.dropdown__option');
    const input = dropdown.querySelector('[data-lead-time-input]');
    const triggerValue = dropdown.querySelector('.dropdown-trigger__value');
    const triggerText = dropdown.querySelector('.dropdown-trigger__text');

    // Initialize rush price from preselected option (e.g. from cart attribute)
    const preselected = dropdown.querySelector('.dropdown__option--selected');
    if (preselected && preselected.dataset.type === 'rush') {
      this.rushPrice = parseFloat(preselected.dataset.price) || 0;
      this.updateDisplayedPrice();
    }

    // Block ATC and show error if preselected option is unavailable
    if (preselected && preselected.dataset.unavailable === 'true') {
      this.rushUnavailable = true;
      this.updateAtcState();
      this.setRushError(true);
    }

    options.forEach(option => {
      option.addEventListener('click', () => {
        const weeks = option.dataset.weeks;
        const type = option.dataset.type;
        const price = option.dataset.price;
        const isUnavailable = option.dataset.unavailable === 'true';

        // Update selection state
        options.forEach(opt => {
          opt.classList.remove('dropdown__option--selected');
          opt.removeAttribute('aria-selected');
        });
        option.classList.add('dropdown__option--selected');
        option.setAttribute('aria-selected', 'true');

        // Update trigger text and alert styling
        if (triggerText) {
          triggerText.classList.toggle('dropdown-trigger__text--alert', isUnavailable);
        }
        if (triggerValue) {
          const displayPrice = price ? price.replace(/\.00$/, '') : price;
          if (type === 'rush') {
            triggerValue.textContent = `Rush ${weeks} weeks${displayPrice && displayPrice !== '0' ? ` + $${displayPrice}` : ''}${isUnavailable ? ' (Unavailable)' : ''}`;
          } else {
            triggerValue.textContent = `Regular ${weeks} weeks`;
          }
        }

        // Block/unblock ATC and rush error based on availability
        this.rushUnavailable = isUnavailable;
        this.updateAtcState();
        this.setRushError(isUnavailable);

        // Update hidden input
        if (input) {
          input.value = weeks;
        }

        // Update estimated-delivery component with new lead weeks
        // Scope to the closest section to avoid hitting cart drawer's estimated-delivery
        const section = this.closest('[id^="shopify-section"]') || document;
        const estimatedDelivery = section.querySelector('estimated-delivery[data-type="mto"]');
        if (estimatedDelivery && weeks) {
          estimatedDelivery.setAttribute('data-lead-weeks', weeks);
        }

        // Update rush price and displayed price
        this.rushPrice = parseFloat(price) || 0;
        this.updateDisplayedPrice();
      });
    });
  }

  setupPolicyCheckbox() {
    const checkbox = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-mto-policy-checkbox]'));
    if (!checkbox) return;
    checkbox.addEventListener('change', () => {
      this.policyAccepted = checkbox.checked;
      this.updateAtcState();
    });
  }

  updateAtcState() {
    const disabled = !this.policyAccepted || this.rushUnavailable;
    const addToCartButton = document.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (addToCartButton) {
      addToCartButton.disabled = disabled;
    }
  }

  /**
   * Shows or hides the rush unavailable error message above the ATC button.
   * @param {boolean} show
   */
  setRushError(show) {
    const atcComponent = document.querySelector('add-to-cart-component');
    if (!atcComponent) return;

    let errorEl = atcComponent.parentElement?.querySelector('.mto-rush-error');

    if (show) {
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'mto-rush-error';
        errorEl.setAttribute('role', 'alert');
        errorEl.textContent = 'Because your cart includes multiple Made-to-Order items with different production timelines, they must be purchased in separate orders. Please place two separate orders to continue.';
        atcComponent.parentElement?.insertBefore(errorEl, atcComponent);
      }
      errorEl.hidden = false;
    } else if (errorEl) {
      errorEl.hidden = true;
    }
  }

  updateCustomizationsAttribute() {
    // Build customizations string: "group1:code1|group2:code2"
    const customizationsArray = [];
    this.customizations.forEach((code, group) => {
      customizationsArray.push(`${group}:${code}`);
    });

    // This will be read by the add-to-cart form
    this.dataset.customizations = customizationsArray.join('|');

    // Dispatch event for other components to listen
    this.dispatchEvent(new CustomEvent('mto-customizations-change', {
      detail: { customizations: customizationsArray.join('|') },
      bubbles: true
    }));
  }

  updateDisplayedPrice() {
    // Calculate total additional price from customizations and rush
    let additionalPrice = 0;
    this.customizationPrices.forEach(price => {
      additionalPrice += price;
    });
    additionalPrice += this.rushPrice;

    const totalPrice = this.basePrice + additionalPrice;

    // Update the price element on the page
    const priceElement = document.querySelector('.price-container .price');
    if (priceElement) {
      // Format price with currency symbol (assuming USD)
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(totalPrice);

      priceElement.textContent = formattedPrice;
    }

    // Dispatch event with updated price
    this.dispatchEvent(new CustomEvent('mto-price-change', {
      detail: {
        basePrice: this.basePrice,
        rushPrice: this.rushPrice,
        customizationsPrice: additionalPrice - this.rushPrice,
        additionalPrice: additionalPrice,
        totalPrice: totalPrice
      },
      bubbles: true
    }));
  }

}

// Register the custom element
if (!customElements.get('mto-options-component')) {
  customElements.define('mto-options-component', MtoOptionsComponent);
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MtoOptionsComponent;
}
